import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import MenuPanel from './Menu/MenuPanel.jsx';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100, 200];

const ICON_BTN =
  'inline-flex items-center justify-center rounded-lg border border-edge bg-surface p-1.5 text-content-secondary transition-colors hover:bg-surface-hover-subtle disabled:cursor-not-allowed disabled:opacity-40';

const BASE_CLASS =
  'flex w-full items-center justify-end gap-10 px-3 py-2 rounded-xl border border-edge ' +
  'bg-[rgba(255,255,255,0.92)] backdrop-blur-[8px] shadow-[0px_4px_12px_rgba(0,0,0,0.08)] ' +
  'dark:bg-[rgba(20,27,42,0.85)] dark:shadow-[0px_4px_12px_rgba(0,0,0,0.35)]';

/**
 * Pagination bar with rows-per-page selector and prev/next navigation.
 *
 * @param {object} props
 * @param {string} [props.rangeLabel] Visible range, e.g. `"1–10"`.
 * @param {number} [props.totalItems]
 * @param {number} [props.currentPage] Accepted for API symmetry; not rendered.
 * @param {number} [props.totalPages] Accepted for API symmetry; not rendered.
 * @param {boolean} props.hasPrevious
 * @param {boolean} props.hasNext
 * @param {number} props.perPage
 * @param {number[]} [props.pageSizeOptions]
 * @param {() => void} props.onPrevPage
 * @param {() => void} props.onNextPage
 * @param {(size: number) => void} props.onPageSizeChange
 * @param {boolean} [props.disabled]
 * @param {string} [props.ariaLabelPerPage] Falls back to `labels.rowsPerPage`.
 * @param {{ rowsPerPage?: string, items?: string, previousPage?: string, nextPage?: string }} [props.labels]
 *        Optional visible/ARIA copy. Defaults stay English so the package is locale-agnostic.
 * @param {string} [props.className]
 *
 * @example
 * <Pagination
 *   rangeLabel="1–10"
 *   totalItems={48}
 *   currentPage={1}
 *   totalPages={5}
 *   hasPrevious={false}
 *   hasNext={true}
 *   perPage={10}
 *   onPrevPage={() => setPage(p => p - 1)}
 *   onNextPage={() => setPage(p => p + 1)}
 *   onPageSizeChange={(size) => setPerPage(size)}
 *   labels={{ rowsPerPage: 'Rows per page:', items: '1–10 of 48 items' }}
 * />
 */
const Pagination = ({
  rangeLabel,
  totalItems,
  currentPage: _currentPage,
  totalPages: _totalPages,
  hasPrevious,
  hasNext,
  perPage,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPrevPage,
  onNextPage,
  onPageSizeChange,
  disabled = false,
  ariaLabelPerPage,
  labels = {},
  className = '',
}) => {
  const rowsPerPageLabel = labels.rowsPerPage ?? 'Rows per page:';
  const itemsLabel = labels.items ?? `${rangeLabel} of ${totalItems} items`;
  const previousPageLabel = labels.previousPage ?? 'Previous page';
  const nextPageLabel = labels.nextPage ?? 'Next page';
  const perPageAria = ariaLabelPerPage ?? rowsPerPageLabel;

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ bottom: 0, right: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const handleToggle = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({
        bottom: window.innerHeight - rect.top + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen((o) => !o);
  };

  const handlePerPageSelect = (size) => {
    onPageSizeChange(size);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  return (
    <div
      className={className ? `${BASE_CLASS} ${className}` : BASE_CLASS}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-content-secondary">{rowsPerPageLabel}</span>
        <span className="text-xs font-medium text-content-secondary">{perPage}</span>
        <button
          ref={triggerRef}
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={perPageAria}
          className={ICON_BTN}
        >
          <ChevronDown size={16} strokeWidth={2} aria-hidden />
        </button>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-content-secondary">
          {itemsLabel}
        </span>

        <button
          type="button"
          onClick={onPrevPage}
          disabled={!hasPrevious || disabled}
          aria-label={previousPageLabel}
          className={ICON_BTN}
        >
          <ChevronLeft size={16} strokeWidth={2} aria-hidden />
        </button>

        <button
          type="button"
          onClick={onNextPage}
          disabled={!hasNext || disabled}
          aria-label={nextPageLabel}
          className={ICON_BTN}
        >
          <ChevronRight size={16} strokeWidth={2} aria-hidden />
        </button>
      </div>

      {createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', bottom: pos.bottom, right: pos.right, zIndex: 9999 }}
          className={open ? 'block' : 'hidden'}
        >
          <MenuPanel role="listbox" ariaLabel={perPageAria} scrollable={false}>
            {pageSizeOptions.map((n) => (
              <button
                key={n}
                type="button"
                role="option"
                aria-selected={n === perPage}
                onClick={() => handlePerPageSelect(n)}
                className={[
                  'flex w-full items-center px-4 py-2 text-left',
                  'text-xs font-medium text-content transition-colors',
                  'hover:bg-[rgba(242,242,242,0.95)] dark:hover:bg-[rgba(32,42,62,0.9)]',
                  n === perPage
                    ? 'bg-[rgba(242,242,242,0.95)] dark:bg-[rgba(32,42,62,0.9)]'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {n}
              </button>
            ))}
          </MenuPanel>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default Pagination;
